/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import { useCallback } from '@web-stories-wp/react';
import PropTypes from 'prop-types';
import { css } from 'styled-components';
import { __, _x, sprintf } from '@web-stories-wp/i18n';
import { DropDown, PLACEMENT } from '@web-stories-wp/design-system';

/**
 * Internal dependencies
 */
import { ZOOM_SETTING } from '../../../constants';
import { useLayout } from '../../../app/layout';

const selectButtonCSS = css`
  height: 36px;
  padding: 8px;

  span {
    padding: 0;
  }
`;

const ZOOM_OPTIONS = [
  {
    label: _x('25%', 'zoom level', 'web-stories'),
    value: 0.25,
  },
  {
    label: _x('50%', 'zoom level', 'web-stories'),
    value: 0.5,
  },
  {
    label: _x('75%', 'zoom level', 'web-stories'),
    value: 0.75,
  },
  {
    label: _x('100%', 'zoom level', 'web-stories'),
    value: 1,
  },
  {
    label: _x('200%', 'zoom level', 'web-stories'),
    value: 2,
  },
  {
    label: _x('400%', 'zoom level', 'web-stories'),
    value: 4,
  },
  {
    label: _x('500%', 'zoom level', 'web-stories'),
    value: 5,
  },
  { label: _x('Fill', 'zoom level', 'web-stories'), value: ZOOM_SETTING.FILL },
  { label: _x('Fit', 'zoom level', 'web-stories'), value: ZOOM_SETTING.FIT },
];

function ZoomSelector({ disabled = false }) {
  const { zoomSetting, zoomLevel, setZoomSetting, setZoomLevel } = useLayout(
    ({
      state: { zoomSetting, zoomLevel },
      actions: { setZoomSetting, setZoomLevel },
    }) => ({
      zoomSetting,
      zoomLevel,
      setZoomSetting,
      setZoomLevel,
    })
  );

  // The drop-down shows the matching option's label; the placeholder only
  // appears for levels without a preset, such as 125% from pinch-to-zoom.
  const selectedValue =
    zoomSetting === ZOOM_SETTING.FIXED ? zoomLevel : zoomSetting;
  // eslint-disable-next-line @wordpress/valid-sprintf -- False positive.
  const placeholder = sprintf(
    /* translators: %d: zoom level percentage value. */
    _x('%d%%', 'zoom level', 'web-stories'),
    Math.round(zoomLevel * 100)
  );

  const handleSetZoom = useCallback(
    (_event, value) => {
      if (Object.prototype.hasOwnProperty.call(ZOOM_SETTING, value)) {
        setZoomSetting(value);
      } else {
        setZoomLevel(value);
      }
    },
    [setZoomSetting, setZoomLevel]
  );

  return (
    <DropDown
      ariaLabel={__('Zoom Level', 'web-stories')}
      disabled={disabled}
      placeholder={placeholder}
      options={ZOOM_OPTIONS}
      placement={PLACEMENT.TOP_START}
      onMenuItemClick={handleSetZoom}
      selectedValue={selectedValue}
      popupFillWidth={false}
      selectButtonStylesOverride={selectButtonCSS}
    />
  );
}

ZoomSelector.propTypes = {
  disabled: PropTypes.bool,
};

export default ZoomSelector;
